import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ChartAudio, ChartPalette, PulseChart, SectionMarker } from '@shared/chart';
import { validateChart } from '@shared/chart';
import { ChartBuilder } from '@shared/engine/pulse/chartBuilder';
import {
  PALETTE_AMBER,
  PALETTE_CRIMSON,
  PALETTE_CYAN,
  PALETTE_MAGENTA,
  PALETTE_VIOLET,
  PALETTE_VOID,
} from '@shared/builtinPulseMaps/palettes';
import { listBuiltinPulseTracks } from '@shared/builtinPulseMaps';
import { authFetch } from '../api';
import { createMap, fetchMapDetail, updateMap } from '../apiClient/maps';
import { useAuth } from '../auth/AuthProvider';
import { MyMapsList } from '../components/MyMapsList';
import { EditorDraftNotice } from '../components/EditorDraftNotice';
import { useEditorDraft } from '../hooks/useEditorDraft';
import { editorDraftKey, isPulseEditorDraft, type PulseEditorDraft } from './editorDraft';
import { saveModePref } from '../game/modePref';
import { modePath } from '../game/modeRouting';
import {
  useEditorAudioPreview,
  type LoopRegionBeats,
} from '../hooks/useEditorAudioPreview';
import {
  useEditorLoopUi,
  buildLoopRegion,
  DEFAULT_LOOP_BEATS,
} from '../hooks/useEditorLoopUi';
import './builder.css';

interface ValidationIssueLike {
  level?: string;
  message?: string;
}

interface SaveErrorBody {
  error?: string;
  issues?: ValidationIssueLike[] | unknown;
  message?: string;
}

/**
 * Turns the API's structured save error into a single human sentence. The
 * server returns either a plain string error (e.g. "slug_taken") or a list
 * of validation issues. We don't want to dump raw JSON at the user.
 */
function formatSaveError(err: { body?: SaveErrorBody; message?: string }): string {
  const body = err.body;
  if (!body) return err.message ?? 'Save failed';
  const code = body.error;
  if (code === 'slug_taken') {
    return 'That slug is already taken - try a different one.';
  }
  if (Array.isArray(body.issues) && body.issues.length > 0) {
    const messages = body.issues
      .filter((i): i is ValidationIssueLike => typeof i === 'object' && i !== null)
      .map((i) => i.message)
      .filter((m): m is string => typeof m === 'string')
      .slice(0, 3);
    if (messages.length > 0) {
      return `Save failed: ${messages.join('; ')}${
        body.issues.length > messages.length ? ' (and more)' : ''
      }`;
    }
  }
  if (code) {
    return `Save failed: ${code}`;
  }
  return err.message ?? 'Save failed';
}

const PALETTE_PRESETS: { name: string; palette: ChartPalette }[] = [
  { name: 'Magenta', palette: PALETTE_MAGENTA },
  { name: 'Cyan', palette: PALETTE_CYAN },
  { name: 'Violet', palette: PALETTE_VIOLET },
  { name: 'Amber', palette: PALETTE_AMBER },
  { name: 'Crimson', palette: PALETTE_CRIMSON },
  { name: 'Void', palette: PALETTE_VOID },
];

function palettesEqual(a: ChartPalette, b: ChartPalette): boolean {
  const keys = ['bg', 'bgAccent', 'player', 'pad', 'padPerfect', 'hazard', 'goal', 'ui'] as const;
  return keys.every((k) => a[k] === b[k]);
}

function findPaletteIndex(p: ChartPalette): number {
  const idx = PALETTE_PRESETS.findIndex((preset) => palettesEqual(preset.palette, p));
  return idx >= 0 ? idx : -1;
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

interface BuilderPad {
  id: string;
  beat: number;
  surface: 'floor' | 'ceiling';
  optional: boolean;
}

const BEAT_SUBDIVISIONS = [1, 2, 4, 8] as const;
const PX_PER_BEAT = 80;
const RAIL_HEIGHT = 220;
const HISTORY_LIMIT = 50;
/**
 * How close (in beats) the click has to be to an onset for snap-to-onset
 * mode to grab it. Generous so a casual click anywhere near an audible hit
 * locks onto it; further away falls through to subdivision snap.
 */
const SNAP_TO_ONSET_RADIUS_BEATS = 0.25;

type SnapValue = (typeof BEAT_SUBDIVISIONS)[number];

interface History {
  past: BuilderPad[][];
  future: BuilderPad[][];
}

// Builder UI lists one entry per song — every variant shares the same
// backing audio + BPM, so showing 36 rows would just be noise. We surface
// the normal variant as the canonical option because its slug (the bare
// song slug, e.g. `'invincible'`) is also what we save into a user-built
// chart's `audio` reference.
const BUILDER_TRACK_OPTIONS = listBuiltinPulseTracks().map((t) => t.variants.normal);
const DEFAULT_AUDIO_SLUG = BUILDER_TRACK_OPTIONS[0].chart.slug;

function pickAudioForSlug(slug: string): { audio: ChartAudio; bpm: number; totalBeats: number } {
  const map =
    BUILDER_TRACK_OPTIONS.find((m) => m.chart.slug === slug) ?? BUILDER_TRACK_OPTIONS[0];
  return { audio: map.chart.audio, bpm: map.chart.bpm, totalBeats: map.chart.totalBeats };
}

export function BuilderPage() {
  const { slug: editingSlug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const initial = pickAudioForSlug(DEFAULT_AUDIO_SLUG);

  const [title, setTitle] = useState('Untitled');
  const [description, setDescription] = useState('A fresh map');
  const [slug, setSlug] = useState('untitled');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [audioSlug, setAudioSlug] = useState<string>(DEFAULT_AUDIO_SLUG);
  const [audio, setAudio] = useState<ChartAudio>(initial.audio);
  const [bpm, setBpm] = useState(initial.bpm);
  const [totalBeats, setTotalBeats] = useState(initial.totalBeats);
  const [difficulty, setDifficulty] = useState(2);
  const [paletteIdx, setPaletteIdx] = useState(0);
  const [customPalette, setCustomPalette] = useState<ChartPalette | null>(null);
  const [pads, setPads] = useState<BuilderPad[]>([]);
  const [history, setHistory] = useState<History>({ past: [], future: [] });
  const [snap, setSnap] = useState<SnapValue>(2);
  const [snapToOnset, setSnapToOnset] = useState(true);
  const [isPublic, setIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedSlug, setSavedSlug] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [validationIssues, setValidationIssues] = useState<string[]>([]);
  const [validationErrorCount, setValidationErrorCount] = useState(0);
  const [padErrorIds, setPadErrorIds] = useState<Set<string>>(new Set());
  const [loadedSlug, setLoadedSlug] = useState<string | null>(null);
  const justSavedSlugRef = useRef<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [hoverBeat, setHoverBeat] = useState<number | null>(null);
  const [hoverSurface, setHoverSurface] = useState<'floor' | 'ceiling' | null>(null);
  const [showShortcuts, setShowShortcuts] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const isDraggingRef = useRef(false);
  const dragModeRef = useRef<'add' | 'remove' | null>(null);
  const dragShiftRef = useRef(false);
  const lastDragKeyRef = useRef<string | null>(null);
  const loopDragRef = useRef<{ anchorBeat: number; region: LoopRegionBeats } | null>(null);

  const preview = useEditorAudioPreview({
    src: audio.src,
    bpm,
    offsetMs: audio.offsetMs,
    totalBeats,
  });
  const loopUi = useEditorLoopUi({
    preview,
    // Forward play starts from whatever's scrolled to the left edge.
    playFromBeat: () => {
      const el = scrollRef.current;
      return el ? Math.max(0, el.scrollLeft / PX_PER_BEAT) : 0;
    },
    // Default loop (L / button) is centred on whatever's scrolled into view.
    defaultLoopRegion: () => {
      const el = scrollRef.current;
      const centreBeat = el ? (el.scrollLeft + el.clientWidth / 2) / PX_PER_BEAT : 0;
      return buildLoopRegion(
        centreBeat - DEFAULT_LOOP_BEATS / 2,
        centreBeat + DEFAULT_LOOP_BEATS / 2,
        totalBeats,
      );
    },
    onError: setSaveError,
  });
  // setLoopDraft drives the in-drag loop preview on the ruler; the toggle refs
  // are read by the once-attached keydown listener.
  const { togglePlay, toggleLoop, setLoopDraft, shownLoopRegion, togglePlayRef, toggleLoopRef } =
    loopUi;

  useEffect(() => {
    if (slug.length === 0 && !slugManuallyEdited) {
      setSlug(`map-${Math.random().toString(36).slice(2, 8)}`);
    }
  }, [slug, slugManuallyEdited]);

  // Builder is Pulse-only — Circles deliberately ships without a UGC
  // editor (we ship a hand-tuned osu-style catalogue). Anyone reaching
  // this page is committing to Pulse for the session.
  useEffect(() => {
    saveModePref('pulse');
  }, []);

  useEffect(() => {
    if (slugManuallyEdited || editingId) return;
    const derived = slugify(title);
    if (derived.length >= 2) {
      setSlug(derived);
    }
  }, [title, slugManuallyEdited, editingId]);

  useEffect(() => {
    if (!editingSlug) return;
    if (!user?.uid) return;
    if (justSavedSlugRef.current === editingSlug) {
      justSavedSlugRef.current = null;
      setLoadedSlug(editingSlug);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const load = async () => {
      try {
        const detail = await fetchMapDetail(editingSlug);
        if (cancelled) return;
        if (detail.authorUid !== user.uid) {
          setSaveError('You can only edit maps you own.');
          return;
        }
        setEditingId(detail.id);
        setTitle(detail.chart.title);
        setDescription(detail.chart.description);
        setSlug(detail.chart.slug);
        setSlugManuallyEdited(true);
        setBpm(detail.chart.bpm);
        setDifficulty(detail.chart.difficulty);
        setTotalBeats(detail.chart.totalBeats);
        setAudio(detail.chart.audio);
        const matchingBuiltin = BUILDER_TRACK_OPTIONS.find(
          (m) => m.chart.audio.src === detail.chart.audio.src,
        );
        setAudioSlug(matchingBuiltin?.chart.slug ?? DEFAULT_AUDIO_SLUG);
        const presetIdx = findPaletteIndex(detail.chart.palette);
        if (presetIdx >= 0) {
          setPaletteIdx(presetIdx);
          setCustomPalette(null);
        } else {
          setCustomPalette(detail.chart.palette);
        }
        setIsPublic(detail.isPublic);
        if (detail.chart.mode !== 'pulse') {
          // The Builder UI only authors Pulse charts; Circles content is
          // sourced from .osu files (see tools/ImportOsuCirclesChart.ts)
          // and isn't user-editable. Surface a clear error rather than
          // silently swallowing the load.
          setSaveError(
            `Map "${detail.chart.slug}" is a ${detail.chart.mode}-mode chart and cannot be opened in the Pulse builder.`,
          );
          return;
        }
        const pulseChart = detail.chart;
        setPads(
          pulseChart.pads.map((p) => ({
            id: p.id,
            beat: p.beat,
            surface: p.surface,
            optional: !!p.optional,
          })),
        );
        setLoadedSlug(editingSlug);
      } catch (err) {
        if (!cancelled) setSaveError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [editingSlug, user?.uid]);

  const palette = customPalette ?? PALETTE_PRESETS[paletteIdx].palette;

  const sortedPads = useMemo(() => [...pads].sort((a, b) => a.beat - b.beat), [pads]);

  const buildCurrentChart = useCallback((): PulseChart => {
    const builder = new ChartBuilder({
      id: slug,
      slug,
      title,
      description,
      bpm,
      difficulty: Math.max(1, Math.min(5, difficulty)) as 1 | 2 | 3 | 4 | 5,
      // User-authored maps live on the bare slug only; the builder UI
      // doesn't surface variant authoring (yet), so every saved community
      // map is the "normal" variant by definition. Hard / Extreme are
      // reserved for builtin tracks.
      variant: 'normal',
      spawnSurface: 'floor',
      palette,
      audio,
    });

    for (const p of sortedPads) {
      if (p.optional) {
        builder.bonusPad(p.beat, { surface: p.surface });
      } else {
        builder.pad(p.beat, { surface: p.surface });
      }
    }

    const sections: SectionMarker[] = [];
    if (totalBeats >= 16) {
      sections.push({ startBeat: 0, endBeat: totalBeats, kind: 'verse' });
    }
    for (const s of sections) builder.section(s);

    builder.setTotalBeats(totalBeats);
    return builder.build();
  }, [slug, title, description, bpm, difficulty, palette, sortedPads, totalBeats, audio]);

  const chart = useMemo(() => buildCurrentChart(), [buildCurrentChart]);
  const draftValue = useMemo<PulseEditorDraft>(() => ({
    mode: 'pulse', title, description, slug, songSlug: audioSlug, audio, bpm, totalBeats,
    difficulty, isPublic, palette, pads,
  }), [title, description, slug, audioSlug, audio, bpm, totalBeats, difficulty, isPublic, palette, pads]);
  const latestDraftRef = useRef(draftValue);
  latestDraftRef.current = draftValue;
  const draft = useEditorDraft({
    storageKey: editorDraftKey(user?.uid ?? '', 'pulse', editingSlug),
    ready: !!user && (!editingSlug || loadedSlug === editingSlug),
    value: draftValue,
    isValid: isPulseEditorDraft,
    onRestore: (restored) => {
      setTitle(restored.title);
      setDescription(restored.description);
      setSlug(editingSlug ?? restored.slug);
      setSlugManuallyEdited(true);
      setAudioSlug(restored.songSlug);
      setAudio(restored.audio);
      setBpm(restored.bpm);
      setTotalBeats(restored.totalBeats);
      setDifficulty(restored.difficulty);
      setIsPublic(restored.isPublic);
      setCustomPalette(restored.palette);
      setPads(restored.pads.map((p) => ({ ...p, optional: !!p.optional })));
      setHistory({ past: [], future: [] });
    },
  });
  const isDirty = draft.isDirty;

  useEffect(() => {
    const result = validateChart(chart);
    const flagged = result.issues.filter(
      (i) => i.level === 'warning' || i.level === 'error',
    );
    setValidationIssues(flagged.map((i) => `${i.level}: ${i.message}`));
    setValidationErrorCount(flagged.filter((i) => i.level === 'error').length);
    const errorIds = new Set<string>();
    for (const issue of flagged) {
      if (issue.level === 'error' && typeof issue.padId === 'string') {
        errorIds.add(issue.padId);
      }
    }
    setPadErrorIds(errorIds);

  }, [chart, editingId]);

  /**
   * If snapToOnset is on, find the closest onset beat within
   * SNAP_TO_ONSET_RADIUS_BEATS (a quarter of a beat - 1/16 note grace).
   * Otherwise, snap to the configured subdivision.
   */
  const snapBeat = useCallback(
    (rawBeat: number): number => {
      if (snapToOnset && audio.onsets.length > 0) {
        let best = audio.onsets[0];
        let bestDiff = Math.abs(best.beat - rawBeat);
        for (let i = 1; i < audio.onsets.length; i++) {
          const o = audio.onsets[i];
          const diff = Math.abs(o.beat - rawBeat);
          if (diff < bestDiff) {
            bestDiff = diff;
            best = o;
          }
        }
        if (bestDiff <= SNAP_TO_ONSET_RADIUS_BEATS) {
          return Math.max(0, Math.min(best.beat, totalBeats - 0.01));
        }
      }
      const denom = snap;
      const snapped = Math.round(rawBeat * denom) / denom;
      const maxBeat = Math.max(0, totalBeats - 1 / denom);
      return Math.max(0, Math.min(snapped, maxBeat));
    },
    [snap, snapToOnset, audio.onsets, totalBeats],
  );

  const padsRef = useRef<BuilderPad[]>(pads);
  useEffect(() => {
    padsRef.current = pads;
  }, [pads]);

  const historyRef = useRef<History>({ past: [], future: [] });
  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  const setPadsWithHistory = useCallback(
    (updater: (prev: BuilderPad[]) => BuilderPad[]) => {
      const before = padsRef.current;
      const next = updater(before);
      if (next === before) return;
      const h = historyRef.current;
      const nextHistory: History = {
        past: [...h.past.slice(-HISTORY_LIMIT + 1), before],
        future: [],
      };
      historyRef.current = nextHistory;
      padsRef.current = next;
      setHistory(nextHistory);
      setPads(next);
    },
    [],
  );

  const undo = useCallback(() => {
    const h = historyRef.current;
    if (h.past.length === 0) return;
    const prev = h.past[h.past.length - 1];
    const current = padsRef.current;
    const nextHistory: History = {
      past: h.past.slice(0, -1),
      future: [...h.future, current],
    };
    historyRef.current = nextHistory;
    padsRef.current = prev;
    lastDragKeyRef.current = null;
    setHistory(nextHistory);
    setPads(prev);
  }, []);

  const redo = useCallback(() => {
    const h = historyRef.current;
    if (h.future.length === 0) return;
    const next = h.future[h.future.length - 1];
    const current = padsRef.current;
    const nextHistory: History = {
      past: [...h.past, current],
      future: h.future.slice(0, -1),
    };
    historyRef.current = nextHistory;
    padsRef.current = next;
    lastDragKeyRef.current = null;
    setHistory(nextHistory);
    setPads(next);
  }, []);

  const applyPadOp = useCallback(
    (
      beat: number,
      surface: 'floor' | 'ceiling',
      mode: 'add' | 'remove' | 'auto',
      optional: boolean,
    ): 'added' | 'removed' | 'noop' => {
      let result: 'added' | 'removed' | 'noop' = 'noop';
      setPadsWithHistory((prev) => {
        const idx = prev.findIndex(
          (p) => Math.abs(p.beat - beat) < 0.05 && p.surface === surface,
        );
        if (mode === 'remove') {
          if (idx >= 0) {
            result = 'removed';
            return prev.filter((_, i) => i !== idx);
          }
          return prev;
        }
        if (mode === 'add') {
          if (idx === -1) {
            result = 'added';
            return [
              ...prev,
              {
                id: `pad-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                beat,
                surface,
                optional,
              },
            ];
          }
          return prev;
        }
        if (idx >= 0) {
          result = 'removed';
          return prev.filter((_, i) => i !== idx);
        }
        result = 'added';
        return [
          ...prev,
          {
            id: `pad-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            beat,
            surface,
            optional,
          },
        ];
      });
      return result;
    },
    [setPadsWithHistory],
  );

  const placePadAtPointer = useCallback(
    (clientX: number, clientY: number, opts: { shift: boolean; mode: 'add' | 'remove' | 'auto' }) => {
      const trackEl = trackRef.current;
      if (!trackEl) return;
      const rect = trackEl.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const beat = snapBeat(x / PX_PER_BEAT);
      const isCeilingClick = y < rect.height / 2;
      const surface: 'floor' | 'ceiling' = isCeilingClick ? 'ceiling' : 'floor';

      const key = `${beat.toFixed(4)}-${surface}`;
      if (lastDragKeyRef.current === key) {
        return;
      }
      lastDragKeyRef.current = key;

      applyPadOp(beat, surface, opts.mode, opts.shift);
    },
    [snapBeat, applyPadOp],
  );

  const handleTrackPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0 && e.button !== 2) return;
      const trackEl = trackRef.current;
      if (!trackEl) return;
      const rect = trackEl.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const beat = snapBeat(x / PX_PER_BEAT);
      const isCeilingClick = y < rect.height / 2;
      const surface: 'floor' | 'ceiling' = isCeilingClick ? 'ceiling' : 'floor';

      const idx = padsRef.current.findIndex(
        (p) => Math.abs(p.beat - beat) < 0.05 && p.surface === surface,
      );
      const mode: 'add' | 'remove' = e.button === 2 || idx >= 0 ? 'remove' : 'add';

      isDraggingRef.current = true;
      dragShiftRef.current = e.shiftKey;
      dragModeRef.current = mode;
      lastDragKeyRef.current = `${beat.toFixed(4)}-${surface}`;

      applyPadOp(beat, surface, mode, e.shiftKey);
    },
    [snapBeat, applyPadOp],
  );

  const handleTrackPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const trackEl = trackRef.current;
      if (!trackEl) return;
      const rect = trackEl.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const beat = snapBeat(x / PX_PER_BEAT);
      const surface: 'floor' | 'ceiling' = y < rect.height / 2 ? 'ceiling' : 'floor';
      setHoverBeat(beat);
      setHoverSurface(surface);
      if (isDraggingRef.current && dragModeRef.current) {
        placePadAtPointer(e.clientX, e.clientY, {
          shift: dragShiftRef.current,
          mode: dragModeRef.current,
        });
      }
    },
    [snapBeat, placePadAtPointer],
  );

  const handleTrackPointerUp = useCallback(() => {
    isDraggingRef.current = false;
    dragModeRef.current = null;
    lastDragKeyRef.current = null;
  }, []);

  const handleTrackPointerLeave = useCallback(() => {
    setHoverBeat(null);
    setHoverSurface(null);
    isDraggingRef.current = false;
    dragModeRef.current = null;
    lastDragKeyRef.current = null;
  }, []);

  // ---- Audio preview (forward + loop) ----
  // Forward playback scrolls the timeline so the playhead stays in view; loop
  // playback keeps the view fixed (the sweeping playhead moves instead) so the
  // author can place pads against the repeating bar.
  useEffect(() => {
    if (!preview.isPlaying || preview.isLooping) {
      return;
    }
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    el.scrollLeft = Math.max(0, preview.playheadBeat * PX_PER_BEAT - el.clientWidth / 3);
  }, [preview.isPlaying, preview.isLooping, preview.playheadBeat]);

  const rulerBeatAt = useCallback(
    (clientX: number, el: HTMLElement): number => {
      const rect = el.getBoundingClientRect();
      return Math.max(0, Math.min((clientX - rect.left) / PX_PER_BEAT, totalBeats));
    },
    [totalBeats],
  );

  const onRulerPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button === 2) {
        preview.clearLoop();
        setLoopDraft(null);
        return;
      }
      if (e.button !== 0) {
        return;
      }
      const beat = rulerBeatAt(e.clientX, e.currentTarget);
      e.currentTarget.setPointerCapture(e.pointerId);
      const region = buildLoopRegion(beat, beat, totalBeats);
      loopDragRef.current = { anchorBeat: beat, region };
      setLoopDraft(region);
    },
    [preview.clearLoop, rulerBeatAt, setLoopDraft, totalBeats],
  );

  const onRulerPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = loopDragRef.current;
      if (!drag) {
        return;
      }
      const region = buildLoopRegion(drag.anchorBeat, rulerBeatAt(e.clientX, e.currentTarget), totalBeats);
      drag.region = region;
      setLoopDraft(region);
    },
    [rulerBeatAt, setLoopDraft, totalBeats],
  );

  const onRulerPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = loopDragRef.current;
      if (!drag) {
        return;
      }
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      loopDragRef.current = null;
      setLoopDraft(null);
      preview.startLoop(drag.region);
    },
    [preview.startLoop, setLoopDraft],
  );

  const removePad = (id: string) => {
    setPadsWithHistory((p) => p.filter((x) => x.id !== id));
  };

  const clearAll = () => {
    if (pads.length === 0) return;
    if (!confirm('Clear all pads? You can undo this.')) return;
    setPadsWithHistory(() => []);
  };

  const changeAudioTrack = useCallback(
    (newSlug: string) => {
      if (newSlug === audioSlug) return;
      const next = pickAudioForSlug(newSlug);
      if (
        padsRef.current.length > 0 &&
        !confirm(
          `Switch backing track to "${newSlug}"? BPM and total beats will reset to match the new track. Pads stay but may need reauthoring.`,
        )
      ) {
        return;
      }
      setLoopDraft(null);
      setAudioSlug(newSlug);
      setAudio(next.audio);
      setBpm(next.bpm);
      setTotalBeats(next.totalBeats);
      const orphans = padsRef.current.filter((p) => p.beat >= next.totalBeats);
      if (orphans.length > 0) {
        setPadsWithHistory((prev) => prev.filter((p) => p.beat < next.totalBeats));
      }
    },
    [audioSlug, setPadsWithHistory],
  );

  const loadTemplate = useCallback(
    (slugId: string) => {
      const map = BUILDER_TRACK_OPTIONS.find((m) => m.chart.slug === slugId);
      if (!map) return;
      if (
        padsRef.current.length > 0 &&
        !confirm(
          `Load "${map.chart.title}" template? This replaces your current pads, BPM, palette, total beats, audio track and difficulty. Cannot be undone.`,
        )
      ) {
        return;
      }
      const fresh = map.chart.pads.map((p, i) => ({
        id: `pad-${Date.now()}-${i}`,
        beat: p.beat,
        surface: p.surface,
        optional: !!p.optional,
      }));
      padsRef.current = fresh;
      lastDragKeyRef.current = null;
      setLoopDraft(null);
      setPads(fresh);
      historyRef.current = { past: [], future: [] };
      setHistory({ past: [], future: [] });
      setBpm(map.chart.bpm);
      setTotalBeats(map.chart.totalBeats);
      setDifficulty(map.chart.difficulty);
      setAudio(map.chart.audio);
      setAudioSlug(map.chart.slug);
      const presetIdx = findPaletteIndex(map.chart.palette);
      if (presetIdx >= 0) {
        setPaletteIdx(presetIdx);
        setCustomPalette(null);
      } else {
        setCustomPalette(map.chart.palette);
      }
    },
    [],
  );

  const onSave = async (playAfterSave = false) => {
    if (savingRef.current || pads.length === 0 || validationErrorCount > 0 || draft.pending || !user) {
      return;
    }
    savingRef.current = true;
    setSaveError(null);
    setSaving(true);
    const canStartPlay = () => {
      if (!playAfterSave) {
        return false;
      }
      if (JSON.stringify(latestDraftRef.current) !== JSON.stringify(draftValue)) {
        setSaveError('Map saved. You made more changes while saving; save and test play again to include them.');
        return false;
      }
      return true;
    };
    try {
      if (editingId) {
        await updateMap(editingId, { chart, isPublic });
        setSavedSlug(slug);
        draft.markSaved(draftValue);
        if (canStartPlay()) {
          navigate(modePath('pulse', `play/${slug}`));
        }
      } else {
        const result = await createMap(chart, isPublic);
        const startPlay = canStartPlay();
        setSavedSlug(result.slug);
        setEditingId(result.id);
        justSavedSlugRef.current = result.slug;
        setLoadedSlug(result.slug);
        setSlug(result.slug);
        setSlugManuallyEdited(true);
        draft.markSaved(draftValue, editorDraftKey(user.uid, 'pulse', result.slug));
        navigate(modePath('pulse', `${startPlay ? 'play' : 'build'}/${result.slug}`), { replace: !startPlay });
      }
    } catch (err) {
      const e = err as { body?: { error?: string; issues?: unknown }; message?: string };
      setSaveError(formatSaveError(e));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onSaveRef.current = onSave;
  });

  const onDelete = async () => {
    if (!editingId) return;
    if (!confirm('Delete this map? This cannot be undone.')) return;
    try {
      const res = await authFetch(`/api/maps/${editingId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      draft.markSaved(draftValue);
      navigate(modePath('pulse', 'browse'));
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const savingRef = useRef(saving);
  useEffect(() => {
    savingRef.current = saving;
  }, [saving]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inField =
        !!target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT');

      if (e.key === 'Escape') {
        setShowShortcuts(false);
        if (inField) (target as HTMLElement).blur();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        if (!savingRef.current && padsRef.current.length > 0) {
          void onSaveRef.current();
        }
        return;
      }

      if (inField) {
        return;
      }

      if (e.key === '?' || (e.key === '/' && e.shiftKey)) {
        e.preventDefault();
        setShowShortcuts((s) => !s);
        return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || e.key === 'Y')) {
        e.preventDefault();
        redo();
        return;
      }
      if (e.key >= '1' && e.key <= '4') {
        const idx = Number(e.key) - 1;
        if (BEAT_SUBDIVISIONS[idx]) setSnap(BEAT_SUBDIVISIONS[idx]);
        return;
      }
      if (e.key === 'p' || e.key === 'P') {
        void onSaveRef.current(true);
        return;
      }
      if (e.key === 'o' || e.key === 'O') {
        setSnapToOnset((s) => !s);
        return;
      }
      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        togglePlayRef.current();
        return;
      }
      if (e.key === 'l' || e.key === 'L') {
        e.preventDefault();
        toggleLoopRef.current();
        return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undo, redo, navigate]);

  const trackWidth = totalBeats * PX_PER_BEAT;

  if (loading) {
    return (
      <div className="loading-screen">
        <p className="muted">Loading editor...</p>
      </div>
    );
  }

  const requiredCount = pads.filter((p) => !p.optional).length;
  const bonusCount = pads.length - requiredCount;
  const onsetBeats = audio.onsets.map((o) => ({ beat: o.beat, kind: o.kind, intensity: o.intensity }));

  return (
    <div className="builder-page" data-testid="builder-page">
      <div className="builder-header">
        <div>
          <h2>
            {editingId ? 'Edit map' : 'Build a map'}
            {isDirty && (
              <span className="dirty-dot" data-testid="dirty-indicator" title="Unsaved changes">
                {' '}
                &bull;
              </span>
            )}
          </h2>
          <p className="muted faint">
            {requiredCount} required &middot; {bonusCount} bonus &middot; {totalBeats} beats
            <button
              type="button"
              className="link-button"
              onClick={() => setShowShortcuts((s) => !s)}
              title="Show keyboard shortcuts"
            >
              shortcuts
            </button>
          </p>
        </div>
        <div className="builder-header-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={undo}
            disabled={history.past.length === 0}
            title="Undo (Ctrl+Z)"
            data-testid="builder-undo"
          >
            Undo
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={redo}
            disabled={history.future.length === 0}
            title="Redo (Ctrl+Shift+Z)"
            data-testid="builder-redo"
          >
            Redo
          </button>
          <button type="button" onClick={() => void onSave(true)} disabled={saving || pads.length === 0 || validationErrorCount > 0 || !!draft.pending} className="btn btn-ghost" data-testid="builder-test-play" title="Save your changes, then play (P)">
            Save &amp; test play
          </button>
          {editingId && (
            <button type="button" className="btn btn-danger" onClick={() => void onDelete()}>
              Delete
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void onSave()}
            disabled={saving || pads.length === 0 || validationErrorCount > 0 || !!draft.pending}
            data-testid="builder-save"
            title={
              validationErrorCount > 0
                ? `Fix ${validationErrorCount} issue${validationErrorCount === 1 ? '' : 's'} before saving`
                : 'Save (Ctrl+S)'
            }
          >
            {saving ? 'Saving...' : editingId ? 'Save changes' : 'Save'}
          </button>
        </div>
      </div>

      <EditorDraftNotice savedAt={draft.pending?.savedAt} isDirty={isDirty} notice={draft.notice} storageError={draft.storageError} onRestore={draft.restore} onDiscard={draft.discard} />
      {saveError && (
        <div className="error-box" data-testid="builder-error" role="alert">
          {saveError}
        </div>
      )}
      {!editingId && <MyMapsList mode="pulse" variant="builder" />}

      <div className="builder-grid">
        <div className="card">
          <h3>Metadata</h3>
          <label>
            Title
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={48}
              data-testid="builder-title"
            />
          </label>
          <label>
            Slug (URL)
            <input
              type="text"
              value={slug}
              onChange={(e) => {
                setSlug(slugify(e.target.value));
                setSlugManuallyEdited(true);
              }}
              pattern="[a-z0-9-]+"
              minLength={2}
              maxLength={40}
              disabled={!!editingId}
              data-testid="builder-slug"
            />
            {!editingId && !slugManuallyEdited && (
              <span className="muted faint">auto from title</span>
            )}
          </label>
          <label>
            Description
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={300}
              rows={3}
            />
          </label>
        </div>

        <div className="card">
          <h3>Music</h3>
          <label>
            Backing track
            <select
              value={audioSlug}
              onChange={(e) => changeAudioTrack(e.target.value)}
              data-testid="builder-audio-track"
            >
              {BUILDER_TRACK_OPTIONS.map((m) => (
                <option key={m.chart.slug} value={m.chart.slug}>
                  {m.chart.title} ({m.chart.bpm} BPM &middot; {m.chart.totalBeats} beats)
                </option>
              ))}
            </select>
            <span className="muted faint">{audio.attribution}</span>
          </label>
          <label>
            BPM
            <input
              type="number"
              value={bpm}
              disabled
              data-testid="builder-bpm"
              title="BPM is locked to the selected backing track."
            />
          </label>
          <label>
            Total beats
            <input
              type="number"
              value={totalBeats}
              disabled
              data-testid="builder-total-beats"
              title="Total beats is locked to the selected backing track."
            />
          </label>
        </div>

        <div className="card">
          <h3>Look</h3>
          <label>
            Difficulty
            <input
              type="range"
              min={1}
              max={5}
              value={difficulty}
              onChange={(e) => setDifficulty(Number(e.target.value))}
            />
            <span>{difficulty} / 5</span>
          </label>
          <label>
            Palette
            <div className="palette-picker">
              {customPalette && (
                <button
                  type="button"
                  className="palette-swatch active"
                  style={{
                    background: `linear-gradient(135deg, ${customPalette.bg}, ${customPalette.bgAccent})`,
                    borderColor: customPalette.player,
                  }}
                  aria-label="Custom (loaded from saved chart)"
                  title="Custom (loaded from saved chart). Click another swatch to replace."
                />
              )}
              {PALETTE_PRESETS.map((p, i) => (
                <button
                  key={p.name}
                  type="button"
                  className={`palette-swatch ${!customPalette && i === paletteIdx ? 'active' : ''}`}
                  onClick={() => {
                    setPaletteIdx(i);
                    setCustomPalette(null);
                  }}
                  style={{
                    background: `linear-gradient(135deg, ${p.palette.bg}, ${p.palette.bgAccent})`,
                    borderColor: p.palette.player,
                  }}
                  aria-label={p.name}
                  title={p.name}
                />
              ))}
            </div>
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              data-testid="builder-public"
            />
            Public (visible in browse)
          </label>
        </div>

        {!editingId && (
          <div className="card">
            <h3>Start from a template</h3>
            <p className="muted faint">Loads a builtin map's pads + style as a starting point.</p>
            <select
              value=""
              onChange={(e) => {
                if (e.target.value) loadTemplate(e.target.value);
              }}
              data-testid="builder-template"
            >
              <option value="">— pick a template —</option>
              {BUILDER_TRACK_OPTIONS.map((m) => (
                <option key={m.chart.slug} value={m.chart.slug}>
                  {m.chart.title} ({m.chart.bpm} BPM &middot; diff {m.chart.difficulty})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="card builder-track-card">
        <div className="builder-track-header">
          <h3>
            Pads <span className="muted">({requiredCount} req &middot; {bonusCount} bonus)</span>
          </h3>
          <div className="builder-track-controls">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={togglePlay}
              data-testid="builder-play"
              title="Play / pause preview (Space)"
            >
              {preview.isPlaying && !preview.isLooping ? 'Pause' : 'Play'}
            </button>
            <button
              type="button"
              className={`btn btn-ghost ${preview.isLooping ? 'active' : ''}`}
              onClick={toggleLoop}
              data-testid="builder-loop"
              aria-pressed={preview.isLooping}
              title="Loop the current section (L) — drag the ruler above the track to pick the bit"
            >
              {preview.isLooping ? 'Stop loop' : 'Loop'}
            </button>
            <span className="builder-control-separator" />
            <span className="muted">Snap:</span>
            {BEAT_SUBDIVISIONS.map((d, i) => (
              <button
                key={d}
                type="button"
                className={`btn btn-ghost ${snap === d ? 'active' : ''}`}
                onClick={() => setSnap(d)}
                title={`1/${d * 4} (${i + 1})`}
                aria-pressed={snap === d}
                disabled={snapToOnset}
              >
                1/{d * 4}
              </button>
            ))}
            <span className="builder-control-separator" />
            <button
              type="button"
              className={`btn btn-ghost ${snapToOnset ? 'active' : ''}`}
              onClick={() => setSnapToOnset((s) => !s)}
              title="Snap clicks to the nearest audio onset (O)"
              aria-pressed={snapToOnset}
              data-testid="builder-snap-onset"
            >
              Snap to onsets
            </button>
            <span className="builder-control-separator" />
            <button
              type="button"
              className="btn btn-ghost"
              onClick={clearAll}
              disabled={pads.length === 0}
              title="Clear all"
            >
              Clear
            </button>
          </div>
        </div>

        <div
          className="builder-track-scroll"
          ref={scrollRef}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div
            className="builder-loop-ruler"
            style={{ width: `${trackWidth}px` }}
            onPointerDown={onRulerPointerDown}
            onPointerMove={onRulerPointerMove}
            onPointerUp={onRulerPointerUp}
            onPointerCancel={onRulerPointerUp}
            aria-label="Loop region selector — drag to choose the bit to loop"
            data-testid="builder-loop-ruler"
          >
            {shownLoopRegion && (
              <div
                className="builder-loop-ruler-region"
                style={{
                  left: `${shownLoopRegion.startBeat * PX_PER_BEAT}px`,
                  width: `${(shownLoopRegion.endBeat - shownLoopRegion.startBeat) * PX_PER_BEAT}px`,
                }}
              />
            )}
            <span className="builder-loop-ruler-label">loop</span>
          </div>
          <div
            className="builder-track"
            ref={trackRef}
            style={
              {
                width: `${trackWidth}px`,
                height: `${RAIL_HEIGHT}px`,
                background: `linear-gradient(180deg, ${palette.bg}, ${palette.bgAccent})`,
              } as React.CSSProperties
            }
            onPointerDown={handleTrackPointerDown}
            onPointerMove={handleTrackPointerMove}
            onPointerUp={handleTrackPointerUp}
            onPointerCancel={handleTrackPointerUp}
            onPointerLeave={handleTrackPointerLeave}
            role="application"
            aria-label="Map editor timeline"
            data-testid="builder-track"
          >
            <div className="rail rail-top" style={{ background: palette.pad }} />
            <div className="rail rail-bottom" style={{ background: palette.pad }} />
            {Array.from({ length: totalBeats + 1 }, (_, i) => (
              <div
                key={i}
                className={`beat-line ${i % 4 === 0 ? 'bar-line' : ''}`}
                style={{ left: i * PX_PER_BEAT }}
              >
                {i % 4 === 0 && <span className="beat-label">{i / 4}</span>}
              </div>
            ))}
            {onsetBeats.map((o, i) => (
              <div
                key={`onset-${i}`}
                className={`onset-tick onset-${o.kind}`}
                style={{
                  left: o.beat * PX_PER_BEAT,
                  opacity: 0.18 + o.intensity * 0.32,
                }}
                title={`${o.kind} @ beat ${o.beat.toFixed(2)}`}
              />
            ))}
            {shownLoopRegion && (
              <div
                className="builder-loop-band"
                style={{
                  left: `${shownLoopRegion.startBeat * PX_PER_BEAT}px`,
                  width: `${(shownLoopRegion.endBeat - shownLoopRegion.startBeat) * PX_PER_BEAT}px`,
                }}
              />
            )}
            {hoverBeat !== null && (
              <div
                className="hover-cursor"
                style={{ left: hoverBeat * PX_PER_BEAT, background: palette.player }}
              >
                <span
                  className="hover-readout"
                  style={{ color: palette.ui }}
                  data-testid="builder-hover-readout"
                >
                  beat {hoverBeat.toFixed(2)}
                </span>
                {hoverSurface && (
                  <span
                    className={`hover-marker ${hoverSurface}`}
                    style={{ background: palette.player }}
                  />
                )}
              </div>
            )}
            {sortedPads.map((p) => {
              const hasError = padErrorIds.has(p.id);
              return (
              <div
                key={p.id}
                className={`pad ${p.optional ? 'pad-optional' : ''} ${p.surface} ${hasError ? 'pad-error' : ''}`}
                style={{
                  left: p.beat * PX_PER_BEAT - 12,
                  background: p.optional ? palette.padPerfect : palette.pad,
                }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  if (e.button === 2) {
                    e.preventDefault();
                    removePad(p.id);
                  }
                }}
                onClick={(e) => {
                  if (e.detail === 2) {
                    e.stopPropagation();
                    removePad(p.id);
                  }
                }}
                title={`Beat ${p.beat.toFixed(2)} (${p.surface}${
                  p.optional ? ' bonus' : ''
                })${hasError ? ' — has validation error' : ''}. Right-click or double-click to remove.`}
              />
              );
            })}
            <div
              className="goal"
              style={{ left: totalBeats * PX_PER_BEAT - 4, background: palette.goal }}
              title="Goal"
            />
            {preview.isPlaying && (
              <div
                className="builder-playhead"
                style={{ left: `${preview.playheadBeat * PX_PER_BEAT}px` }}
              />
            )}
          </div>
        </div>
        <p className="muted faint builder-instructions">
          Click a rail to place a pad &middot; drag along to paint a row &middot; right-click or
          double-click to remove &middot; <kbd>Shift</kbd>+click for a bonus pad &middot; drag the
          ruler above the track (or <kbd>L</kbd>) to loop a bar while you place &middot;{' '}
          <kbd>Space</kbd> to preview &middot; press <kbd>?</kbd> for shortcuts
        </p>
      </div>

      {validationIssues.length > 0 && (
        <div className="card">
          <h3>Validation</h3>
          <ul className="validation-list">
            {validationIssues.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </div>
      )}

      {savedSlug && !saveError && !isDirty && (
        <div className="card builder-saved" data-testid="builder-saved">
          Saved! <Link to={modePath('pulse', `play/${savedSlug}`)}>Play it</Link> or share{' '}
          <code>{window.location.origin}{modePath('pulse', `play/${savedSlug}`)}</code>
        </div>
      )}

      {showShortcuts && (
        <div
          className="shortcut-overlay"
          onClick={() => setShowShortcuts(false)}
          data-testid="builder-shortcut-overlay"
        >
          <div className="card shortcut-card" onClick={(e) => e.stopPropagation()}>
            <h3>Keyboard shortcuts</h3>
            <ul>
              <li><kbd>1</kbd>-<kbd>4</kbd> Snap 1/4 / 1/8 / 1/16 / 1/32</li>
              <li><kbd>O</kbd> Toggle snap-to-onset</li>
              <li><kbd>Space</kbd> Play / pause preview</li>
              <li><kbd>L</kbd> Loop the current section</li>
              <li><kbd>Ctrl</kbd>+<kbd>Z</kbd> Undo &middot; <kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>Z</kbd> Redo</li>
              <li><kbd>Ctrl</kbd>+<kbd>S</kbd> Save</li>
              <li><kbd>P</kbd> Save and test play</li>
              <li><kbd>Shift</kbd>+click Bonus pad</li>
              <li>Right-click pad Remove &middot; right-click ruler clears loop</li>
              <li><kbd>?</kbd> Toggle this</li>
            </ul>
            <button type="button" className="btn" onClick={() => setShowShortcuts(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
